import React from "react";
import {
  AWAITING_RESPONDANT,
  RESPONDANT_ONBOARDED,
  NEGOTIATION_ONGOING,
  NEGOTIATION_REACHED,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  CLAIMENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
  AWAITING_RESPONDANT_QUIT,
  awaitingRespondentAcceptance,
} from "../../../helpers/constants";
import CommentBox from "./common/CommentBox";
import CaseAgreement from "./common/CaseAgreement";
import AgreementDocCard from "./common/AgreementDocCard";
import _ from "lodash";

const MediationState = React.memo(
  ({
    currentState,
    isRespondent,
    ownerId,
    caseDetails,
    showCta,
    comments,
    setComments,
    partyList = [],
  }) => {
    const RenderCommentBox = ({
      status,
      expand = true,
      errorText,
      comments,
      caseId,
      setComments,
      disabled = false,
    }) => (
      <CommentBox
        title={`${_.capitalize(caseDetails?.resolutionKind)} Process`}
        expand={expand}
        status={status}
        errorText={errorText}
        ownerId={ownerId}
        caseId={caseId}
        comments={comments}
        disabled={disabled}
        setComments={setComments}
        partyList={partyList}
        kind={
          caseDetails?.resolutionKind === "mediation"
            ? "Mediator"
            : "Arbitrator"
        }
      />
    );

    const AgreementSection = () => (
      <>
        <div style={{ marginTop: 28 }}>
          <CaseAgreement
            title={`Final Award Summary for Case ID: ${caseDetails?.id}`}
            message={caseDetails?.negotiationSummary}
            summaryStatus={caseDetails?.summaryStatus === "completed"}
            isPaid={currentState?.cta?.length === 0}
          />
        </div>
        <div style={{ marginTop: 28 }}>
          <AgreementDocCard
            id={caseDetails?.id}
            status={caseDetails.status}
            isRespondent={isRespondent}
            hasRating={caseDetails?.rating}
            hasAgreement={caseDetails?.hasAgreementDocument}
            type={`Download`}
          />
        </div>
      </>
    );

    const renderCommentBoxWithError = (errorText, disabled = true) => (
      <div style={{ marginTop: 28 }}>
        <RenderCommentBox
          caseId={caseDetails?.id}
          expand={true}
          status={"error"}
          errorText={errorText}
          comments={comments}
          setComments={setComments}
          disabled={disabled}
        />
      </div>
    );

    const renderDefaultCommentBox = (expand = true, disabled = false) => (
      <div style={{ marginTop: 28 }}>
        <RenderCommentBox
          caseId={caseDetails?.id}
          expand={expand}
          comments={comments}
          setComments={setComments}
          disabled={disabled}
        />
      </div>
    );

    const renderCommentBoxWithAgreement = () => (
      <>
        {renderDefaultCommentBox(false, true)}
        <AgreementSection />
      </>
    );

    const respondentDeclinedConfig = {
      status: caseDetails.respondentStatus === "declined" ? "error" : undefined,
      errorText: "Respondent Declined the Case",
      disabled: true,
    };

    const stateConfig = {
      [AWAITING_RESPONDANT]: () => (
        <div style={{ marginTop: 28 }}>
          <RenderCommentBox
            caseId={caseDetails?.id}
            expand={true}
            {...respondentDeclinedConfig}
            comments={comments}
            setComments={setComments}
          />
        </div>
      ),
      [awaitingRespondentAcceptance]: () => (
        <div style={{ marginTop: 28 }}>
          <RenderCommentBox
            caseId={caseDetails?.id}
            expand={true}
            {...respondentDeclinedConfig}
            comments={comments}
            setComments={setComments}
          />
        </div>
      ),
      [AWAITING_RESPONDANT_QUIT]: () =>
        renderCommentBoxWithError("Case Closed by Scrutiny Clerk"),
      [RESPONDANT_ONBOARDED]: () => (
        <div style={{ marginTop: 28 }}>
          <RenderCommentBox
            caseId={caseDetails?.id}
            expand={true}
            {...respondentDeclinedConfig}
            comments={comments}
            setComments={setComments}
          />
        </div>
      ),
      [NEGOTIATION_ONGOING]: () => renderDefaultCommentBox(),
      [NEGOTIATION_QUIT_BY_CLAIMENT]: () =>
        renderCommentBoxWithError("Case Quit by Claimant"),
      [NEGOTIATION_QUIT_BY_RESPONDENT]: () =>
        renderCommentBoxWithError("Case Quit by Respondent"),
      [NEGOTIATION_QUIT_BY_CASEMANAGER]: () =>
        renderCommentBoxWithError("Case Closed by Scrutiny Clerk"),
      [NEGOTIATION_REACHED]: () => renderDefaultCommentBox(true, true),
      [CLAIMENT_PAID_NEGOTIATION]: renderCommentBoxWithAgreement,
      [RESPONDENT_PAID_NEGOTIATION]: renderCommentBoxWithAgreement,
      [BOTH_PAID_NEGOTIATION]: renderCommentBoxWithAgreement,
      finalAward: renderCommentBoxWithAgreement,
      reservedForAward: () => renderDefaultCommentBox(false),
      awardPassed: () => renderDefaultCommentBox(false),
      resolutionFailed: () => renderDefaultCommentBox(false),
      settledAndWithdrawn: () => renderDefaultCommentBox(false),
      updatedAndWithdrawn: () => renderDefaultCommentBox(false),
    };

    // Default states for arbitration-related flows
    const arbitrationStates = [
      "noticeToArbitrate",
      "arbitrationReferenced",
      "arbitrationNominated",
      "arbitrationConfirmed",
      "waitingForArbitratorConfirmation",
      "firstNoticeSent",
      "statementOfClaimFiled",
      "awaitingForSec17Details",
      "sec17PetitionFiled",
      "sec17Ordered",
      "awaitingSec17Execution",
      "sec17ExtensionMemoFiled",
      "sec17ExtensionMemoOrdered",
      "fsOrdered",
      "attachmentOrdered",
      "garnisheeProhibitaryOrder",
      "garnisheeFinalOrder",
      "sec17Closed",
      "statementOfDefenceFiled",
      "rejoinderFromClaimant",
      "surrejoinderFromRespondent",
      "2ndNoticeMOM",
      "evidenceFiledByClaimant",
      "evidenceFiledByRespondent",
      "crossExamination",
      "argumentsByClaimant",
      "argumentsByRespondent",
      "negotiationOngoing",
      "firstHearing",
      "secondHearing",
      "thirdHearing",
      "firstMom",
      "commencementLetter",
      "arbitrationInprogress",
      "referenceLetter",
    ];

    // Add arbitration states to stateConfig
    arbitrationStates.forEach((state) => {
      stateConfig[state] = () => renderDefaultCommentBox();
    });

    const renderState = stateConfig[currentState?.key] || (() => null);

    return renderState();
  }
);

export default MediationState;
